import { Flex, ProductColor, Typography, TypographyProps } from '@aloft/toolkit-sui';

import { useWatch } from 'react-hook-form';

import { AlertTargetReadOnlyCell } from '../../../components/PropertyGrid/fields/AlertTargetReadOnlyCell.tsx';
import { readOnlyFieldTypographyProps } from '../../../components/PropertyGrid/fields/styles.ts';
import { PropertyGridSectionProps } from '../../../components/PropertyGrid/types.ts';
import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';

const Separator = () => (
  <Flex width="100%" height="1px" gridColumn="1 / -1" backgroundColor="textPrimary" />
);

export const AdjustmentsGridAdjustedSPSection = ({
  columnIndex,
  fieldTypes,
  control,
}: PropertyGridSectionProps) => {
  const propertyId = useWatch({
    name: `columns.${columnIndex}.propertyId`,
    control,
  });

  const compAdjustments = useAppSelector(selectCompAdjustments);
  const compAdjustment = compAdjustments[propertyId];
  const { adjustedSalePrice, grossAdjustmentPct, netAdjustment, netAdjustmentPct } =
    compAdjustment ?? {};

  const showLabels = fieldTypes.includes('label');
  const showValues =
    fieldTypes.includes('readOnlyValue') || fieldTypes.includes('readOnlyArvValue');
  const showReadOnlyUnitRate = fieldTypes.includes('readOnlyUnitRate');
  const valueGridColumn = showLabels ? '2 / -1' : '1 / -1';

  const typographyProps: TypographyProps = {
    ...readOnlyFieldTypographyProps,
    __dangerDoNotUseSx: {
      ...readOnlyFieldTypographyProps.__dangerDoNotUseSx,
      gridColumn: valueGridColumn,
    },
  };

  return (
    <>
      <Separator />
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Net Adj. Total</Typography>}
      {showValues && (
        <Typography
          {...typographyProps}
          color={netAdjustment?.adjustment?.getDisplayColor() as ProductColor | undefined}
          textAlign="right"
        >
          {netAdjustment?.adjustment?.toString(true) ?? '-'}
        </Typography>
      )}
      {showReadOnlyUnitRate && <Typography {...typographyProps}>-</Typography>}
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Net Adj. %</Typography>}
      {showValues && (
        <AlertTargetReadOnlyCell
          cellKeyId="netAdjustmentPct"
          gridColumn={valueGridColumn}
          propertyId={propertyId}
        >
          {netAdjustmentPct?.value ?? '-'}
        </AlertTargetReadOnlyCell>
      )}
      {showReadOnlyUnitRate && <Typography {...typographyProps}>-</Typography>}
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Gross Adj. %</Typography>}
      {showValues && (
        <AlertTargetReadOnlyCell
          cellKeyId="grossAdjustmentPct"
          gridColumn={valueGridColumn}
          propertyId={propertyId}
        >
          {grossAdjustmentPct?.value ?? '-'}
        </AlertTargetReadOnlyCell>
      )}
      {showReadOnlyUnitRate && <Typography {...typographyProps}>-</Typography>}
      <Separator />
      {showLabels && <Typography {...readOnlyFieldTypographyProps}>Adjusted SP</Typography>}
      {showValues && (
        <AlertTargetReadOnlyCell
          cellKeyId="adjustedSalePrice"
          gridColumn={valueGridColumn}
          propertyId={propertyId}
          color={adjustedSalePrice?.adjustment?.getDisplayColor() as ProductColor | undefined}
        >
          {adjustedSalePrice?.value ?? '-'}
        </AlertTargetReadOnlyCell>
      )}
      {showReadOnlyUnitRate && <Typography {...typographyProps}>-</Typography>}
    </>
  );
};
